// 치과 재내원 안내(리콜) — Cloud Functions 호출 모음 (2026-10-01)
// 서버 계약: docs/계약_리콜_서버_2026-10-01.md §B(callable)·§C(트리거). 계약이 바뀌면 이 파일 한 곳만 고친다.
// 화면 컴포넌트는 CF 이름·입출력 형태를 직접 알지 않는다. 내보내기·수동 발송·재발송 CF는 없다(계약 B-3).
// 공통: region asia-northeast3, onCall. 플래그(config/features.clinicRecall) OFF면 failed-precondition {code:'feature-off'}.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

// 리콜 발송 규칙 안내문 — 기획서 §4-2(ClinicRecalls 상단·알림 파트 A 하단 공용). 문구 변경 시 법적보안 확인
export const RECALL_RULE_NOTICE = '앱을 설치하고 재내원 안내에 동의한 환자에게만 치과 명의로 (광고) 표기 알림이 갑니다. 발송 시각 매일 오전 10시 10분, 예정 7일 전·당일·14일 뒤·60일 뒤 최대 4회, 환자가 응답하면 중단됩니다.';
export const RECALL_GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

// 트리거 코드 → 치과 화면 라벨 (계약 §C). 환자 화면·푸시에는 이 라벨이 나가지 않는다(서버 템플릿 고정) — 치과 데스크 식별용.
export const TRIGGER_LABEL = Object.freeze({
    R1: '정기 점검', R2: '스케일링 점검', R3: '임플란트 점검', R4a: '보철 관리', R4b: '보철 점검', R5: '치료 이어가기', R6: '조정 시기',
});
export const TRIGGER_CODES = Object.freeze(Object.keys(TRIGGER_LABEL));
export const triggerLabel = (code) => TRIGGER_LABEL[code] || code || '-';

// 트리거별 서버 허용 주기(개월) — 이 값이면 periodMonths 로, 아니면 dueAt(날짜)로 보낸다 (계약 B-1: 주기와 dueAt 동시 지정 불가)
const MONTH_ALLOWED = Object.freeze({ R1: [3, 6, 12], R2: [6, 12], R3: [3, 6, 12], R4a: [1], R4b: [6, 12] });
export const DUE_MIN_DAYS = 7;      // 계약 B-1 하한
export const DUE_MAX_DAYS = 731;    // 계약 B-1 상한(24개월)

export const STAGE_LABEL = Object.freeze({ pre: '사전 안내(7일 전)', due: '예정일 안내', again1: '재안내 1', again2: '재안내 2', snooze: '추가 안내' });
export const stageLabel = (row) => {
    if (!row) return '-';
    if (row.status === 'scheduled' && (row.sendCount || 0) === 0 && !row.lastResult) return '대기(발송 전)';
    return STAGE_LABEL[row.stageCode] || (row.status === 'scheduled' ? '대기(발송 전)' : '-');
};

export const STATUS_LABEL = Object.freeze({ scheduled: '대기', sent: '보냄', acknowledged: '회신', snoozed: '회신', expired: '종료', optout: '수신 거부' });
export const EXPIRED_REASON_LABEL = Object.freeze({ no_response: '응답 없음', not_delivered: '전달 안 됨', new_visit: '새 방문으로 종료', cancelled: '치과가 중단', replaced: '시기 변경으로 교체', held_expired: '발송 보류 중 기한 지나 종료' });
export const statusLabel = (row) => {
    if (!row) return '-';
    const base = STATUS_LABEL[row.status] || row.status || '-';
    if (row.status === 'expired' && row.expiredReason) return `${base} · ${EXPIRED_REASON_LABEL[row.expiredReason] || row.expiredReason}`;
    return base;
};

// 회신 표시 — 환자 버튼 2개(확인했어요/나중에)만. 그 외는 '-'
export const replyLabel = (row) => {
    if (!row) return '-';
    if (row.status === 'acknowledged') return '확인했어요';
    if (row.status === 'snoozed') return row.snoozeUntil ? '나중에(90일 뒤 1회 더)' : '나중에';
    if (row.status === 'optout') return '수신 거부';
    return '-';
};

// 마지막 시도 결과(건너뜀 포함) — 치과가 "왜 안 갔는지" 보는 용도
export const RESULT_LABEL = Object.freeze({
    ok: '발송됨', no_consent: '재내원 안내 미동의', consent_expired: '동의 2년 경과(재확인 필요)', notify_off: '환자가 앱 알림 끔', no_optin: '환자가 치과 안내 끔',
    no_device: '앱 미설치', no_contact: '대표전화 미등록(발송 안 됨)', no_sender: '치과 이름(업체명) 미등록 — 설정 › 프로필에서 등록',
    failed: '발송 실패', rate_limited: '일 상한 초과', clinic_blocked: '치과 승인 상태 확인 필요',
    clinic_unpaid: '구독 확인 후 발송 재개', clinic_rejected: '치과 가입 승인 필요(발송 보류)', send_gate_unavailable: '발송 보류(확인 중 — 다음 날 다시 시도)',
    banned: '치과명 문구 검사 보류(관리자 문의)', error: '오류',
});   // 계약 §RESULTS 13종(5차: no_sender 추가)
export const resultLabel = (code) => code ? (RESULT_LABEL[code] || code) : '';

export const BUCKETS = Object.freeze([
    { key: 'this_week', label: '이번 주 예정' },
    { key: 'this_month', label: '이번 달 예정' },
    { key: 'sent_no_reply', label: '보냄 · 미응답' },
    { key: 'replied', label: '회신' },
    { key: 'no_device', label: '앱 미설치' },
    { key: 'no_consent', label: '동의 없음' },
    // 2026-10-17 점검 P2: 환자 수신거부(optout) — 서버 listClinicRecalls 7번째 버킷(clinic-recall.js BUCKETS·classifyRecall). 전엔 어느 구간에도 안 들어가 목록에서 조용히 사라졌다
    { key: 'optout', label: '수신 거부' },
]);

// 로컬(KST 사용 전제) 날짜 → YYYY-MM-DD
export const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const addMonths = (d, n) => { const x = new Date(d.getFullYear(), d.getMonth() + n, d.getDate()); if (x.getDate() !== d.getDate()) x.setDate(0); return x; };   // 말일 보정(1/31+1개월=2/28)
export const daysAhead = (dayStr) => {
    const [y, m, dd] = String(dayStr || '').split('-').map(Number);
    if (!y || !m || !dd) return NaN;
    const t = new Date(); const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    return Math.round((new Date(y, m - 1, dd) - today) / 86400000);
};
export const fmtDay = (dayStr) => dayStr ? String(dayStr).replace(/-/g, '.') : '-';

// CF 오류 → 화면 문구. 원본은 console.error (조용한 실패 금지)
export function describeRecallError(e, fallback = RECALL_GENERIC_ERROR) {
    console.error('[clinicRecallApi]', e?.code || '', e?.message || e, e?.details || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    const c = e?.details?.code;
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    if (e?.code === 'functions/permission-denied') return e?.message || '권한이 없습니다.';
    if (e?.code === 'functions/resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/failed-precondition') {
        if (c === 'feature-off') return '재내원 안내 기능이 아직 열리지 않았습니다.';
        // 계약 B-1(5차 R-P1-4): 약관 바목(재내원 안내 위탁) 재확인 전에는 생성이 거부된다 — 재확인 UI 전까지 모든 치과가 이 코드
        if (c === 'delegation-reconfirm-required') return '개정 약관(재내원 안내 위탁 항목) 재확인 후 등록할 수 있습니다. 약관 재확인 배너에서 동의해 주세요.';
        return e?.message || fallback;
    }
    if (e?.code === 'functions/invalid-argument') {
        if (c === 'due-out-of-range') return `안내 예정일은 오늘부터 ${DUE_MIN_DAYS}일 이후 ~ 24개월 이내여야 합니다.`;
        if (c === 'invalid-trigger') return '안내 종류를 선택해 주세요.';
        if (c === 'invalid-period' || c === 'invalid-due') return '안내 시기 값을 확인해 주세요.';
        return e?.message || '입력값을 확인해 주세요.';
    }
    if (e?.code === 'functions/not-found') return '환자 정보를 찾을 수 없습니다. 목록을 새로고침해 주세요.';
    return fallback;
}

// 발송 보류 사유(계약 B-1·B-3 응답 플래그) → ['phone'|'name'|'banned'] — 저장 직후·목록 상단 안내 공용
export function clinicSendBlockers(res) {
    const out = [];
    if (res?.clinicPhoneMissing === true) out.push('phone');
    if (res?.clinicNameMissing === true) out.push('name');
    if (res?.clinicNameBanned === true) out.push('banned');
    return out;
}
export const BLOCKER_SHORT = Object.freeze({ phone: '대표전화 미등록(발송 안 됨)', name: '치과 이름 미등록(발송 안 됨)', banned: '치과 이름 표현 검토 필요(발송 보류)' });

// 계약 B-3 — { ok, bucket, count, truncated, counts:{ack,snooze}, items:[row], clinicPhoneMissing, clinicNameMissing, clinicNameBanned } (행에도 같은 세 플래그 — 5차)
export async function listClinicRecalls(bucket) {
    const res = await call('listClinicRecalls')({ bucket });
    return res.data || { ok: false, items: [], count: 0, truncated: false, counts: {} };
}

// 계약 B-1 — 다음 안내 시기 설정. months 가 트리거 허용값이면 periodMonths, 아니면 dueDay(YYYY-MM-DD)를 dueAt 으로.
// source 'manual' = 같은 트리거의 활성 리콜만 expired(replaced) 후 새로 생성. 출력 { ok, recallId, dueDay, expired, clinicPhoneMissing, clinicNameMissing, clinicNameBanned }(5차 R-P2-7)
export async function setPatientRecall({ patientId, trigger, months, dueDay, source = 'manual' }) {
    const payload = { patientId, trigger, source };
    if (months && Array.isArray(MONTH_ALLOWED[trigger]) && MONTH_ALLOWED[trigger].includes(months)) payload.periodMonths = months;
    else if (dueDay) payload.dueAt = dueDay;
    else throw Object.assign(new Error('안내 시기가 없습니다'), { code: 'functions/invalid-argument', details: { code: 'invalid-due' } });
    const res = await call('setPatientRecall')(payload);
    return res.data || { ok: false };
}

// 계약 B-2 — 활성이면 expired(cancelled). 이미 종료면 멱등 { ok, already, status }
export async function cancelPatientRecall(patientId, recallId) {
    const res = await call('cancelPatientRecall')({ patientId, recallId });
    return res.data || { ok: false };
}

// ─────────────────────────────────────────────────────────────────────────────
// 알림 파트 A — 치과 기본 설정·일괄 예약 (2026-10-08, 기획서_환자자동관리_알림파트_2026-10-07 §4-1·§5)
//   서버 계약: docs/계약_알림파트_서버_2026-10-08.md §A (대조 완료 2026-10-08). 응답 필드는 없을 때 안전한 빈 상태로 처리. 계약이 바뀌면 이 블록만 고친다.
// ─────────────────────────────────────────────────────────────────────────────

// 트리거 카탈로그 폴백 — 서버 clinic-recall.js TRIGGERS(unit·defaultPeriod·allowed)와 같은 값. getClinicRecallDefaults 응답의 catalog 가 있으면 그것을 우선한다
export const TRIGGER_CATALOG_FALLBACK = Object.freeze({
    R1: { unit: 'months', defaultPeriod: 6, allowed: [3, 6, 12] },
    R2: { unit: 'months', defaultPeriod: 12, allowed: [6, 12] },
    R3: { unit: 'months', defaultPeriod: 6, allowed: [3, 6, 12] },
    R4a: { unit: 'months', defaultPeriod: 1, allowed: [1] },
    R4b: { unit: 'months', defaultPeriod: 12, allowed: [6, 12] },
    R5: { unit: 'days', defaultPeriod: 60, allowed: [30, 60, 90] },
    R6: { unit: 'weeks', defaultPeriod: 5, allowed: [4, 5, 6] },
});
const UNIT_SUFFIX = Object.freeze({ months: '개월', days: '일', weeks: '주' });
export const periodLabel = (unit, n) => `${n}${UNIT_SUFFIX[unit] || ''}`;

// 기본 설정 표 설명(치과 화면 전용 1줄 — 환자 노출 0)
export const TRIGGER_DESC = Object.freeze({
    R1: '정기 검진 시기 안내', R2: '스케일링 시기 안내', R3: '임플란트 정기 점검 시기 안내', R4a: '보철 장착 후 첫 점검 안내',
    R4b: '보철 정기 점검 시기 안내', R5: '중단된 치료를 이어갈 시기 안내', R6: '교정 장치 조정 시기 안내',
});

export const RECALL_BULK_MAX = 100;          // 1회 상한(대표 결정 Q4)
export const BASIS_LOOKBACK_DAYS = 90;       // 기준일 = 오늘 ~ 90일 전(서버 validateRecallInput)

// 응답 catalog(있으면) + 폴백 → { R1:{unit,defaultPeriod,allowed[]} … } (순수)
//   계약 A-1: catalog = [{ trigger, unit, defaultPeriod, allowed[] }] 배열(7행). 객체(코드 키) 형태도 받아 준다.
export function normalizeTriggerCatalog(raw) {
    const byCode = Array.isArray(raw)
        ? raw.reduce((o, r) => { if (r && typeof r.trigger === 'string') o[r.trigger] = r; return o; }, {})
        : (raw && typeof raw === 'object' ? raw : {});
    const out = {};
    for (const code of TRIGGER_CODES) {
        const fb = TRIGGER_CATALOG_FALLBACK[code];
        const r = byCode[code] || null;
        const allowed = Array.isArray(r?.allowed) && r.allowed.every(n => Number.isFinite(n)) && r.allowed.length ? r.allowed.slice() : fb.allowed.slice();
        const unit = UNIT_SUFFIX[r?.unit] ? r.unit : fb.unit;
        const defaultPeriod = allowed.includes(r?.defaultPeriod) ? r.defaultPeriod : (allowed.includes(fb.defaultPeriod) ? fb.defaultPeriod : allowed[0]);
        out[code] = { unit, defaultPeriod, allowed };
    }
    return out;
}

// 응답 defaults(있으면) → { R1:{enabled:boolean, period:number} … } (순수). 없거나 형식 오류인 키는 "켜짐 + 카탈로그 기본 주기"(서버 미설정 = 현행 동작과 동일)
export function normalizeRecallDefaults(raw, catalog = normalizeTriggerCatalog(null)) {
    const out = {};
    for (const code of TRIGGER_CODES) {
        const c = catalog[code];
        const r = raw && typeof raw === 'object' ? raw[code] : null;
        out[code] = {
            enabled: typeof r?.enabled === 'boolean' ? r.enabled : true,
            period: c.allowed.includes(Number(r?.period)) ? Number(r.period) : c.defaultPeriod,
        };
    }
    return out;
}

// 계약 A-1 getClinicRecallDefaults — 오너·직원. 응답 { ok, role, canEdit, defaults{7키}, catalog[7행], updatedAt(ms|null), history[] }
//   필드가 없으면 폴백 카탈로그·전부 켜짐(현행)으로. 반환 { defaults, catalog, canEdit(null=응답에 없음), updatedAt(ms|null), raw }
export async function getClinicRecallDefaults() {
    const res = await call('getClinicRecallDefaults')({});
    const d = res.data || {};
    const catalog = normalizeTriggerCatalog(d.catalog || null);
    const defaults = normalizeRecallDefaults(d.defaults || null, catalog);
    const upd = d.updatedAt;
    const updatedAt = Number.isFinite(upd) ? upd : (upd && Number.isFinite(upd._seconds) ? upd._seconds * 1000 : null);
    return { defaults, catalog, canEdit: typeof d.canEdit === 'boolean' ? d.canEdit : null, updatedAt, raw: d };
}

// 계약 A-2 setClinicRecallDefaults — 대표만(직원 permission-denied {code:'owner-only'}). 입력 { defaults:{R1:{enabled,period},…} } 7키 전부(부분 병합 허용 — 전부 보내도 같음).
//   응답 { ok, defaults{7키}, changed[] } · 검증 실패 invalid-argument {code:'invalid-defaults'}(아무것도 저장 안 함)
export async function setClinicRecallDefaults(defaults) {
    const payload = {};
    for (const code of TRIGGER_CODES) {
        const v = defaults?.[code];
        if (!v) continue;
        payload[code] = { enabled: v.enabled === true, period: Number(v.period) };
    }
    const res = await call('setClinicRecallDefaults')({ defaults: payload });
    return res.data || { ok: false };
}

// 계약 A-3 setPatientRecallsBulk({ pids, trigger, basisDay?, period?, dryRun? }) — 오너·직원, 1~100명.
//   dryRun:true = 미리보기(저장 0) → { dueDay, items[{pid, decision, selectable, fcmRegistered}], counts{total,selectable}, dailyUsed, dailyMax }
//   실행 = 환자별 트랜잭션, 즉시 발송 없음 → { dueDay, created[{pid,recallId}], skipped[{pid,reason}], failed[{pid,code}], counts, dailyUsed, dailyMax, clinicPhoneMissing… }
//   오류 details.code: too-many-pids · trigger-disabled · invalid-trigger|invalid-basis|invalid-period|due-out-of-range · delegation-reconfirm-required · bulk-daily-cap(resource-exhausted, remaining)
function bulkPayload({ pids, trigger, basisDay, period }) {
    const list = Array.isArray(pids) ? [...new Set(pids.filter(p => typeof p === 'string' && p))] : [];
    if (list.length === 0) throw Object.assign(new Error('대상 환자가 없습니다'), { code: 'functions/invalid-argument', details: { code: 'empty-pids' } });
    if (list.length > RECALL_BULK_MAX) throw Object.assign(new Error(`한 번에 ${RECALL_BULK_MAX}명까지 예약할 수 있습니다`), { code: 'functions/invalid-argument', details: { code: 'too-many-pids' } });
    const payload = { pids: list, trigger };
    if (basisDay) payload.basisDay = basisDay;
    if (Number.isFinite(Number(period))) payload.period = Number(period);
    return payload;
}
const pidRow = (x) => (x && typeof x.pid === 'string' ? x : null);

export async function previewPatientRecallsBulk(args) {
    const res = await call('setPatientRecallsBulk')({ ...bulkPayload(args), dryRun: true });
    const d = res.data || {};
    if (d.dryRun !== true) {
        // 서버가 dryRun 을 무시했다면 실제로 저장됐을 수 있다 — 숨기지 않고 오류로 올린다
        console.error('[clinicRecallApi] 일괄 미리보기 응답에 dryRun:true 없음', Object.keys(d));
        throw Object.assign(new Error('미리보기 응답이 올바르지 않습니다. 재내원 안내 화면에서 예약 여부를 확인해 주세요.'), { code: 'functions/internal' });
    }
    return {
        dueDay: typeof d.dueDay === 'string' ? d.dueDay : '',
        items: (Array.isArray(d.items) ? d.items : []).map(pidRow).filter(Boolean)
            .map(x => ({ pid: x.pid, decision: x.decision || '', selectable: x.selectable === true, fcmRegistered: x.fcmRegistered !== false })),
        dailyUsed: Number.isFinite(d.dailyUsed) ? d.dailyUsed : null,
        dailyMax: Number.isFinite(d.dailyMax) ? d.dailyMax : null,
    };
}

export async function setPatientRecallsBulk(args) {
    const res = await call('setPatientRecallsBulk')(bulkPayload(args));
    const d = res.data || {};
    return {
        dueDay: typeof d.dueDay === 'string' ? d.dueDay : '',
        done: (Array.isArray(d.created) ? d.created : []).map(pidRow).filter(Boolean),
        skipped: (Array.isArray(d.skipped) ? d.skipped : []).map(pidRow).filter(Boolean),
        failed: (Array.isArray(d.failed) ? d.failed : []).map(pidRow).filter(Boolean).map(x => ({ ...x, reason: x.reason || x.code || 'error' })),
        dailyUsed: Number.isFinite(d.dailyUsed) ? d.dailyUsed : null,
        dailyMax: Number.isFinite(d.dailyMax) ? d.dailyMax : null,
        blockers: clinicSendBlockers(d),
        raw: d,
    };
}

// 일괄·기본 설정 오류 → 화면 문구(계약 A-2·A-3 details.code). 그 밖은 기존 describeRecallError
export function describeRecallBulkError(e, fallback = RECALL_GENERIC_ERROR) {
    const c = e?.details?.code;
    if (c === 'trigger-disabled') { console.error('[clinicRecallApi]', e?.code, c); return '치과 기본 설정에서 꺼 둔 안내 종류는 일괄 예약할 수 없습니다.'; }
    if (c === 'bulk-daily-cap') { console.error('[clinicRecallApi]', e?.code, c); return `오늘 일괄 예약 상한(하루 500명)을 넘습니다.${Number.isFinite(e?.details?.remaining) ? ` 오늘 남은 수: ${e.details.remaining}명` : ''}`; }
    if (c === 'too-many-pids') { console.error('[clinicRecallApi]', e?.code, c); return `한 번에 ${RECALL_BULK_MAX}명까지 예약할 수 있습니다.`; }
    if (c === 'invalid-basis') { console.error('[clinicRecallApi]', e?.code, c); return `기준일은 오늘부터 ${BASIS_LOOKBACK_DAYS}일 전까지 고를 수 있습니다.`; }
    if (c === 'invalid-defaults') { console.error('[clinicRecallApi]', e?.code, c); return '기본 설정 값이 올바르지 않습니다. 화면을 새로고침한 뒤 다시 저장해 주세요.'; }
    if (c === 'owner-only') { console.error('[clinicRecallApi]', e?.code, c); return '치과 대표만 할 수 있습니다.'; }
    return describeRecallError(e, fallback);
}

// 일괄 예약 건너뜀 사유(서버 consentDecision·레코드 판정) → 화면 문구. 모르는 코드는 서버 코드를 그대로(조용히 숨기지 않음)
export const BULK_SKIP_LABEL = Object.freeze({
    no_consent: '재내원 안내 동의 없음', consent_expired: '재내원 안내 동의 없음(2년 경과)', notify_off: '알림 꺼짐', no_optin: '재내원 안내 수신 꺼짐',
    no_device: '앱 없음', inactive: '등록 상태 아님(삭제·요청 중)', not_found: '환자 정보 없음', 'not-found': '환자 정보 없음',
    trigger_off: '치과 기본 설정에서 꺼진 종류', daily_limit: '오늘 일괄 예약 상한 초과', error: '오류',
});
export const bulkSkipLabel = (code) => BULK_SKIP_LABEL[code] || RESULT_LABEL[code] || code || '사유 미상';

// 명부 레코드(normalizeRecord) → 사전 표시(참고) — 서버가 최종 판정. 동의 없음만 선택 비활성(기획 §4-1 A-2)
export function recallPrecheck(r) {
    if (!r || r.status !== 'active') return { selectable: false, reason: 'inactive' };
    if (!r.recallConsentOn) return { selectable: false, reason: 'no_consent' };
    if (!r.notifyOn) return { selectable: true, reason: 'notify_off' };
    if (!r.recallOptIn) return { selectable: true, reason: 'no_optin' };   // 계약 A-3: notify_off·no_optin·앱 없음은 선택 가능(예약되고 발송 때 건너뜀)
    if (r.appNotMissing === false) return { selectable: true, reason: 'no_device' };   // 서버 기준(appNotMissing) — 명부 배지용 fcmRegistered 와 분리
    return { selectable: true, reason: 'ok' };
}

// 기본 설정 조회·저장 오류 문구 — not-found 를 "환자 정보 없음"으로 오인하지 않게 fallback 으로(함수 미배포·문서 없음 등). 원본은 console.error
export function describeRecallDefaultsError(e, fallback = RECALL_GENERIC_ERROR) {
    if (e?.code === 'functions/not-found') {
        console.error('[clinicRecallApi] 기본 설정', e?.code, e?.message || e, e?.details || '');
        return fallback;
    }
    return describeRecallBulkError(e, fallback);
}
